import type { JSONContent } from '@tiptap/core'

/**
 * Unchecked to-do items in a note's TipTap JSON, in document order (design
 * 10a: "your unchecked to-dos from the last note"). Nested to-dos are carried
 * as their own items; checked ones — and anything inside them — stay behind.
 */
export function extractOpenTodos(contentJson: string | null | undefined): Array<JSONContent> {
  if (!contentJson) return []
  let doc: JSONContent
  try {
    doc = JSON.parse(contentJson) as JSONContent
  } catch {
    return []
  }

  const open: Array<JSONContent> = []
  const walk = (node: JSONContent) => {
    if (node.type === 'taskItem') {
      if (node.attrs?.checked === true) return
      // Keep the item's own text blocks; nested lists are walked separately.
      const ownContent = (node.content ?? []).filter((child) => child.type !== 'taskList')
      if (ownContent.some((child) => textOf(child).trim().length > 0)) {
        open.push({ type: 'taskItem', attrs: { checked: false }, content: ownContent })
      }
    }
    for (const child of node.content ?? []) walk(child)
  }
  walk(doc)
  return open
}

export function textOf(node: JSONContent): string {
  if (node.type === 'text') return node.text ?? ''
  return (node.content ?? []).map(textOf).join('')
}

/** A fresh note body holding the carried-over to-dos, then a blank line. */
export function buildCarryOverDoc(items: Array<JSONContent>): JSONContent {
  return {
    type: 'doc',
    content: [{ type: 'taskList', content: items }, { type: 'paragraph' }],
  }
}
